/**
 * Subscription change ConfirmationModal
 *
 * Shared by both startup-subscription and investor-subscription pages.
 * Renders a centered modal with an icon (upgrade / downgrade / destructive),
 * an optional tier badge, a message, and Cancel / Confirm buttons.
 */

import React from 'react';
import { View, Text, Pressable, StyleSheet, Modal } from 'react-native';
import Animated, { SlideInUp } from 'react-native-reanimated';
import { ArrowDownCircle, ArrowUpCircle, X } from 'lucide-react-native';
import { useThemeColors } from '@/lib/useThemeColors';

export interface ConfirmationModalProps {
  visible: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  isDestructive?: boolean;
  isUpgrade?: boolean;
  tierName?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmationModal({
  visible,
  title,
  message,
  confirmText,
  cancelText,
  isDestructive = false,
  isUpgrade = false,
  tierName,
  onConfirm,
  onCancel,
}: ConfirmationModalProps) {
  const colors = useThemeColors();

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onCancel}
    >
      <Pressable style={modalStyles.backdrop} onPress={onCancel}>
        <Pressable style={modalStyles.contentWrapper} onPress={(e) => e.stopPropagation()}>
          <Animated.View
            entering={SlideInUp.springify().damping(20)}
            style={[
              modalStyles.container,
              {
                backgroundColor: colors.surface.primary,
                shadowColor: '#000',
              },
            ]}
          >
            {/* Icon */}
            <View
              style={[
                modalStyles.iconContainer,
                {
                  backgroundColor: isDestructive
                    ? '#FEE2E2'
                    : isUpgrade
                      ? colors.primary[50]
                      : '#E0F2FE',
                },
              ]}
            >
              {isDestructive ? (
                <X size={28} color="#DC2626" strokeWidth={2.5} />
              ) : isUpgrade ? (
                <ArrowUpCircle size={28} color={colors.primary[600]} strokeWidth={2} />
              ) : (
                <ArrowDownCircle size={28} color="#0284C7" strokeWidth={2} />
              )}
            </View>

            {/* Title */}
            <Text style={[modalStyles.title, { color: colors.text.primary }]}>
              {title}
            </Text>

            {/* Tier Badge */}
            {tierName && (
              <View
                style={[
                  modalStyles.tierBadge,
                  {
                    backgroundColor: isUpgrade ? colors.primary[50] : '#F0F9FF',
                    borderColor: isUpgrade ? colors.primary[200] : '#BAE6FD',
                  },
                ]}
              >
                <Text
                  style={[
                    modalStyles.tierBadgeText,
                    { color: isUpgrade ? colors.primary[700] : '#0369A1' },
                  ]}
                >
                  {tierName}
                </Text>
              </View>
            )}

            {/* Message */}
            <Text style={[modalStyles.message, { color: colors.text.secondary }]}>
              {message}
            </Text>

            {/* Buttons — View wrapper pattern (RN 0.81 Pressable callback style bug) */}
            <View style={modalStyles.buttonContainer}>
              <View style={[modalStyles.button, { backgroundColor: colors.surface.secondary, overflow: 'hidden' }]}>
                <Pressable onPress={onCancel} style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                  <Text style={[modalStyles.cancelButtonText, { color: colors.text.secondary }]}>
                    {cancelText}
                  </Text>
                </Pressable>
              </View>

              <View style={[modalStyles.button, { backgroundColor: isDestructive ? '#DC2626' : colors.primary[600], overflow: 'hidden' }]}>
                <Pressable onPress={onConfirm} style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}>
                  <Text style={modalStyles.confirmButtonText}>{confirmText}</Text>
                </Pressable>
              </View>
            </View>
          </Animated.View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const modalStyles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  contentWrapper: {
    width: '100%',
    maxWidth: 400,
  },
  container: {
    borderRadius: 20,
    padding: 28,
    alignItems: 'center',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.15,
    shadowRadius: 30,
    elevation: 10,
  },
  iconContainer: {
    width: 60,
    height: 60,
    borderRadius: 30,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 20,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
  },
  tierBadge: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    borderWidth: 1,
    marginBottom: 12,
  },
  tierBadgeText: {
    fontSize: 15,
    fontWeight: '600',
  },
  message: {
    fontSize: 15,
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 24,
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: 12,
    width: '100%',
  },
  button: {
    flex: 1,
    height: 48,
    borderRadius: 12,
  },
  cancelButtonText: {
    fontSize: 15,
    fontWeight: '600',
  },
  confirmButtonText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
